import { DateTime, Interval } from 'luxon'
import useIconAccessor from 'components/lesson-type-category/icons'
import {
  getScheduleEventColor,
  getScheduleEventType,
  SCHEDULE_EVENT_TYPES,
} from 'pages/pga-coach/dashboard/todays-schedule/schedule-event-colors'
import { endOfUsWeek, startOfUsWeek } from '../schedule-date-ranges'

export const CALENDAR_VIEWS = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  LIST: 'list',
}

export const CALENDAR_VIEW_OPTIONS = [
  { value: CALENDAR_VIEWS.DAY, label: 'Day' },
  { value: CALENDAR_VIEWS.WEEK, label: 'Week' },
  { value: CALENDAR_VIEWS.MONTH, label: 'Month' },
  { value: CALENDAR_VIEWS.LIST, label: 'List' },
]

export const MOBILE_CALENDAR_VIEW_OPTIONS = [
  { value: CALENDAR_VIEWS.MONTH, label: 'Month' },
  { value: CALENDAR_VIEWS.LIST, label: 'List' },
]

export const getMobileCalendarView = view =>
  view === CALENDAR_VIEWS.LIST ? CALENDAR_VIEWS.LIST : CALENDAR_VIEWS.MONTH

export const HOUR_HEIGHT = 64
export const HOUR_HEIGHT_MOBILE = 52
export const DESKTOP_CALENDAR_MIN_WIDTH = 640
export const DEFAULT_GRID_START_HOUR = 0
export const DEFAULT_GRID_END_HOUR = 24
/** Preferred first scroll position for day/week grids (8am). */
export const DEFAULT_GRID_SCROLL_HOUR = 8
export const MONTH_VISIBLE_EVENTS = 3

/** US-style week labels: Sunday → Saturday (matches academy schedule). */
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export const getCalendarDateRange = (view, anchorDate = DateTime.local()) => {
  switch (view) {
    case CALENDAR_VIEWS.DAY:
      return {
        start: anchorDate.startOf('day'),
        end: anchorDate.endOf('day'),
      }
    case CALENDAR_VIEWS.WEEK:
    case CALENDAR_VIEWS.LIST:
      return {
        start: startOfUsWeek(anchorDate),
        end: endOfUsWeek(anchorDate),
      }
    case CALENDAR_VIEWS.MONTH: {
      const monthStart = anchorDate.startOf('month')
      const monthEnd = anchorDate.endOf('month')

      return {
        start: startOfUsWeek(monthStart),
        end: endOfUsWeek(monthEnd),
      }
    }
    default:
      return {
        start: startOfUsWeek(anchorDate),
        end: endOfUsWeek(anchorDate),
      }
  }
}

export const navigateCalendarDate = (view, anchorDate, direction) => {
  const unit =
    view === CALENDAR_VIEWS.MONTH
      ? 'month'
      : view === CALENDAR_VIEWS.DAY
        ? 'day'
        : 'week'

  return direction === 'prev'
    ? anchorDate.minus({ [unit]: 1 })
    : anchorDate.plus({ [unit]: 1 })
}

export const formatToolbarDateLabel = anchorDate =>
  anchorDate.toFormat('LLL d, yyyy')

export const getCalendarRangeLabel = (view, anchorDate) => {
  const { start, end } = getCalendarDateRange(view, anchorDate)

  switch (view) {
    case CALENDAR_VIEWS.DAY:
      return start.toFormat('cccc, LLL d')
    case CALENDAR_VIEWS.MONTH:
      return anchorDate.toFormat('LLLL yyyy')
    case CALENDAR_VIEWS.WEEK:
    case CALENDAR_VIEWS.LIST:
      if (start.month === end.month) {
        return `${start.toFormat('MMM d')} – ${end.toFormat('d, yyyy')}`
      }

      return `${start.toFormat('MMM d')} – ${end.toFormat('MMM d, yyyy')}`
    default:
      return start.toFormat('MMM d, yyyy')
  }
}

export const getBookingDateTime = (booking, field = 'startDateTime') => {
  const value = booking[field]
  const zone = booking.timezone

  return zone ? DateTime.fromISO(value, { zone }) : DateTime.fromISO(value)
}

export const getBookingEventKey = booking =>
  `${booking.id}-${booking.startDateTime}`

export const getBookingDisplay = (booking, iconAccessor) => {
  const eventType = getScheduleEventType(booking)

  if (eventType === SCHEDULE_EVENT_TYPES.BLOCKED_TIME) {
    return {
      title: booking.title || 'Blocked time',
      subtitle: formatEventTimeRangeLabel(booking),
      eventType,
    }
  }

  if (booking.__typename === 'Session') {
    const student = booking.enrollments[0]?.student
    const category = booking.lessonType?.category
    const categoryMeta = category ? iconAccessor[category] : null
    const fullName =
      student && `${student.firstName} ${student.lastName}`.trim()

    return {
      title: fullName || booking.title,
      subtitle: categoryMeta?.text || 'Private Lesson',
      eventType,
    }
  }

  const badgeLabels = {
    [SCHEDULE_EVENT_TYPES.FAMILY_GOLF]: 'Family Golf',
    [SCHEDULE_EVENT_TYPES.PGA_JR_LEAGUE]: 'PGA Jr. League',
    [SCHEDULE_EVENT_TYPES.GROUP_EVENT]: iconAccessor.GROUP.text,
  }

  return {
    title: booking.title,
    subtitle: badgeLabels[eventType] || iconAccessor.GROUP.text,
    eventType,
  }
}

export const useBookingDisplay = booking => {
  const iconAccessor = useIconAccessor()

  return getBookingDisplay(booking, iconAccessor)
}

export const groupBookingsByDate = bookings => {
  return bookings.reduce((acc, booking) => {
    const dateKey = getBookingDateTime(booking).toISODate()

    if (!acc[dateKey]) {
      acc[dateKey] = []
    }

    acc[dateKey].push(booking)
    return acc
  }, {})
}

export const MAX_DAY_EVENT_DOTS = 3

export const getDayEventDotColors = (
  bookingsByDate,
  day,
  maxDots = MAX_DAY_EVENT_DOTS,
) => {
  const bookings = bookingsByDate[day.toISODate()] || []
  const seenTypes = new Set()
  const colors = []

  for (const booking of bookings) {
    const eventType = getScheduleEventType(booking)

    if (seenTypes.has(eventType)) {
      continue
    }

    seenTypes.add(eventType)
    colors.push(getScheduleEventColor(eventType))

    if (colors.length >= maxDots) {
      break
    }
  }

  return colors
}

export const getWeekDays = anchorDate =>
  Array.from({ length: 7 }, (_, index) =>
    startOfUsWeek(anchorDate).plus({ days: index }),
  )

export const getMonthGridDays = anchorDate => {
  const { start, end } = getCalendarDateRange(CALENDAR_VIEWS.MONTH, anchorDate)
  const days = []
  let current = start

  while (current <= end) {
    days.push(current)
    current = current.plus({ days: 1 })
  }

  return days
}

export const formatHourLabel = hour => {
  if (hour === 0 || hour === 24) {
    return '12a'
  }

  if (hour === 12) {
    return '12p'
  }

  if (hour < 12) {
    return `${hour}a`
  }

  return `${hour - 12}p`
}

export const computeVisibleHourRange = (
  bookings,
  {
    defaultStart = DEFAULT_GRID_START_HOUR,
    defaultEnd = DEFAULT_GRID_END_HOUR,
  } = {},
) => {
  if (!bookings.length) {
    return { startHour: defaultStart, endHour: defaultEnd }
  }

  let minHour = defaultStart
  let maxHour = defaultEnd

  bookings.forEach(booking => {
    const start = getBookingDateTime(booking)
    const end = getBookingDateTime(booking, 'endDateTime')

    if (!start.isValid || !end.isValid) {
      return
    }

    minHour = Math.min(minHour, start.hour)
    maxHour = Math.max(maxHour, end.hour + (end.minute > 0 ? 1 : 0))
  })

  return {
    startHour: Math.max(0, minHour),
    endHour: Math.min(24, Math.max(maxHour, defaultEnd)),
  }
}

export const getEventGridPosition = (booking, startHour, hourHeight) => {
  const start = getBookingDateTime(booking)
  const end = getBookingDateTime(booking, 'endDateTime')
  const startOffset = start.hour + start.minute / 60 - startHour
  const durationHours = Interval.fromDateTimes(start, end).length('hours')

  return {
    top: Math.max(0, startOffset * hourHeight),
    height: Math.max(hourHeight * 0.75, durationHours * hourHeight),
  }
}

export const formatMonthEventLabel = (booking, display) => {
  const timeLabel = formatMonthEventTimeLabel(booking)

  return `${timeLabel} ${display?.title || booking.title}`
}

export const formatEventTimeLabel = booking => {
  const start = getBookingDateTime(booking)

  return start.toFormat('h:mm a')
}

export const formatEventTimeRangeLabel = booking => {
  const start = getBookingDateTime(booking)
  const end = getBookingDateTime(booking, 'endDateTime')

  return `${start.toFormat('h:mm a')} – ${end.toFormat('h:mm a')}`
}

export const formatMonthEventTimeLabel = booking => {
  const start = getBookingDateTime(booking)

  return start.minute === 0 ? start.toFormat('h a') : start.toFormat('h:mm a')
}

export const getDayLabel = date => DAY_LABELS[date.weekday % 7]

export const isSameDay = (left, right) => left.hasSame(right, 'day')

export const isToday = date => isSameDay(date, DateTime.local())

export const isViewingToday = (view, anchorDate, today = DateTime.local()) =>
  isSameDay(anchorDate, today)

export const isCurrentMonth = (date, anchorDate) =>
  date.month === anchorDate.month && date.year === anchorDate.year

export const isHourSlotOccupied = (dayBookings, day, hour) => {
  const slotStart = day.set({ hour, minute: 0, second: 0, millisecond: 0 })
  const slotEnd = slotStart.plus({ hours: 1 })

  return dayBookings.some(booking => {
    if (getScheduleEventType(booking) === SCHEDULE_EVENT_TYPES.BLOCKED_TIME) {
      return false
    }

    const start = getBookingDateTime(booking)
    const end = getBookingDateTime(booking, 'endDateTime')

    return start < slotEnd && end > slotStart
  })
}

export const isPastBookSlot = (day, hour = null) => {
  const slotStart =
    hour == null
      ? day.startOf('day')
      : day.set({ hour, minute: 0, second: 0, millisecond: 0 })

  return slotStart < DateTime.local().startOf('hour')
}

export const buildBookStudentUrl = (bookingUrl, day, hour = null) => {
  if (!bookingUrl) {
    return null
  }

  const start =
    hour == null
      ? day.set({ hour: 9, minute: 0, second: 0, millisecond: 0 })
      : day.set({ hour, minute: 0, second: 0, millisecond: 0 })

  try {
    const url = bookingUrl.startsWith('http')
      ? new URL(bookingUrl)
      : new URL(bookingUrl, window.location.origin)

    url.searchParams.set('startDateTime', start.toISO())

    return url.toString()
  } catch {
    return bookingUrl
  }
}
